import { i18n } from '@fs/zion-locale'

import { buildRecordTypes, findPropertyValue, getReworkStatusLabel } from 'helpers'
import getFormattedDate, { dateFormats } from 'helpers/dateFormatHelper'
import DownloadCsvConfig from './DownloadCsvConfig'

export default class AllRecordTypesCsv extends DownloadCsvConfig {
  static columns = [
    { key: 'imageGroupNumber', header: i18n.t('AllRecordTypesCsv.header.imageGroupNumber', 'Image Group Number') },
    { key: 'naturalGroupId', header: i18n.t('AllRecordTypesCsv.header.naturalGroupId', 'Natural Group ID') },
    { key: 'externalId', header: i18n.t('AllRecordTypesCsv.header.externalId', 'Capture ID') },
    { key: 'cameraOperator', header: i18n.t('AllRecordTypesCsv.header.cameraOperator', 'Camera Operator') },
    { key: 'projectId', header: i18n.t('AllRecordTypesCsv.header.projectId', 'Project ID') },
    { key: 'mediaNumber', header: i18n.t('AllRecordTypesCsv.header.mediaNumber', 'Media Number') },
    { key: 'weekEndingDate', header: i18n.t('AllRecordTypesCsv.header.weekEndingDate', 'Week Ending Date') },
    { key: 'place', header: i18n.t('AllRecordTypesCsv.header.place', 'Place') },
    { key: 'date', header: i18n.t('AllRecordTypesCsv.header.date', 'Date') },
    { key: 'recordType', header: i18n.t('AllRecordTypesCsv.header.recordType', 'Record Type') },
    { key: 'imageCount', header: i18n.t('AllRecordTypesCsv.header.imageCount', 'Image Count') },
    { key: 'imagesIndexed', header: i18n.t('AllRecordTypesCsv.header.imagesIndexed', 'Images Indexed') },
    { key: 'title', header: i18n.t('AllRecordTypesCsv.header.title', 'Title') },
    { key: 'volume', header: i18n.t('AllRecordTypesCsv.header.volume', 'Volume') },
    { key: 'language', header: i18n.t('AllRecordTypesCsv.header.language', 'Language') },
    { key: 'creator', header: i18n.t('AllRecordTypesCsv.header.creator', 'Creator') },
    { key: 'custodian', header: i18n.t('AllRecordTypesCsv.header.custodian', 'Custodian') },
    {
      key: 'archivalReferenceNumber',
      header: i18n.t('AllRecordTypesCsv.header.archivalReferenceNumber', 'Archival Reference Number'),
    },
    { key: 'captureDate', header: i18n.t('AllRecordTypesCsv.header.captureDate', 'Capture Date') },
    { key: 'createdDate', header: i18n.t('AllRecordTypesCsv.header.createdDate', 'Date Created') },
    { key: 'active', header: i18n.t('AllRecordTypesCsv.header.active', 'Availability Status') },
    { key: 'imageRework', header: i18n.t('AllRecordTypesCsv.header.imageRework', 'Image Rework') },
    { key: 'imageLink', header: i18n.t('AllRecordTypesCsv.header.imageLink', 'Image Link') },
    { key: 'rmsId', header: i18n.t('AllRecordTypesCsv.header.rmsId', 'RMS ID') },
    {
      key: 'cameraOperatorId',
      header: i18n.t('AllRecordTypesCsv.header.cameraOperatorId', 'Camera Operator ID'),
    },
    {
      key: 'cameraOperatorCisId',
      header: i18n.t('AllRecordTypesCsv.header.cameraOperatorCisId', 'Camera Operator CIS ID'),
    },
  ]

  static filename = i18n.t('AllRecordTypesCsv.filename.searchResults', 'searchResults')

  async generateRowsWithStream(groups = [], getImageDetailsUrl = '', languageMapper = (l) => l) {
    // eslint-disable-next-line no-restricted-syntax
    for (const group of groups) {
      const propertyValue = (propertyName) => findPropertyValue(propertyName, group.properties)

      const rowToPush = {
        archivalReferenceNumber: group.metadata?.archivalReferenceNumber,
        cameraOperator: propertyValue('cameraOperator'),
        cameraOperatorId: propertyValue('cameraOperatorNumber'),
        cameraOperatorCisId: propertyValue('cameraOperator_Id'),
        captureDate: getFormattedDate(group.captureDate?.fromTimestamp, null, dateFormats.formatted),
        createdDate: getFormattedDate(group.metadata?.createdDate?.fromTimestamp, null, dateFormats.formatted),
        creator: group.metadata?.creator?.join(', '),
        custodian: group.metadata?.custodian?.join(', '),
        date: getFormattedDate(group.dateRange?.fromTimestamp, group.dateRange?.toTimestamp, dateFormats.formatted),
        imageCount: group.childCount,
        imageGroupNumber: group.metadata?.imageGroupNumber?.padStart(9, '0'),
        imageLink: getImageDetailsUrl(group),
        imageRework: getReworkStatusLabel(group.lastImageRework?.state),
        imagesIndexed: group.indexedChildCount,
        language: group.metadata?.languages?.map((l) => languageMapper(l)).join(', '),
        mediaNumber: propertyValue('originalMediaNumber'),
        naturalGroupId: group.naturalGroupId,
        externalId: group.externalId,
        place: [...new Set(group.coverages?.map((coverage) => coverage.place?.fullName))].filter(Boolean).join('; '),
        projectId: group.phoenixAcquisitionIds?.[0],
        recordType: buildRecordTypes(group.coverages),
        rmsId: group.id,
        title: group.metadata?.title,
        volume: group.metadata?.volume,
        weekEndingDate: getFormattedDate(
          propertyValue('originalWeekEnding') ? propertyValue('originalWeekEnding') : null,
          null,
          dateFormats.formatted
        ),
      }

      rowToPush.active = getActiveStatus(group.active)

      if (this.doDownloadStream) {
        // eslint-disable-next-line no-await-in-loop
        await this.writeToDownloadStream(rowToPush)
      } else {
        this.rows.push(rowToPush)
      }
    }
  }
}

function getActiveStatus(active) {
  return active ? i18n.t('AllRecordTypesCsv.active', 'Active') : i18n.t('AllRecordTypesCsv.withdrawn', 'Withdrawn')
}
